import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../styles/ReviewManager.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const ReviewManager = ({
  taskName,
  taskId,
  referenceId = null,
  strictReference = false,
  userId,
  isAdmin,
  onReviewed,
  setGlobalLoading,
  refreshTrigger,
}) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("not_completed");
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [reviewDetails, setReviewDetails] = useState(null);
  const refIdValue = referenceId != null ? String(referenceId) : null;

  const formatReviewedAt = (value) => {
    if (!value) return "";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "";

    const date = dt.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
    const time = dt.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${date} ${time}`;
  };

  const fetchStatus = async () => {
    if (strictReference && refIdValue == null) {
      setStatus("not_completed");
      setReviewDetails(null);
      return;
    }

    try {
      const res = await axios.get(`${API_BASE}/api/usertasks/status/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const idKey =
        taskId != null
          ? refIdValue != null
            ? `${taskId}_${refIdValue}`
            : String(taskId)
          : null;
      const nameKey =
        taskName != null
          ? refIdValue != null
            ? `${taskName}_${refIdValue}`
            : taskName
          : null;
      const taskStatus =
        (idKey && res.data?.status?.[idKey]) ??
        (nameKey && res.data?.status?.[nameKey]) ??
        (strictReference ? undefined : res.data?.status?.[String(taskId)]) ??
        (strictReference ? undefined : res.data?.status?.[taskName]) ??
        "not_completed";
      const taskReviewDetails =
        (idKey && res.data?.reviewDetails?.[idKey]) ??
        (nameKey && res.data?.reviewDetails?.[nameKey]) ??
        (strictReference ? undefined : res.data?.reviewDetails?.[String(taskId)]) ??
        (strictReference ? undefined : res.data?.reviewDetails?.[taskName]) ??
        null;
      const taskSignDate =
        (idKey && res.data?.signdates?.[idKey]) ??
        (nameKey && res.data?.signdates?.[nameKey]) ??
        (strictReference ? undefined : res.data?.signdates?.[String(taskId)]) ??
        (strictReference ? undefined : res.data?.signdates?.[taskName]) ??
        null;
      const resolvedReviewDetails =
        taskReviewDetails ||
        ((taskStatus === "completed" || taskStatus === "almost_expired") &&
        taskSignDate
          ? {
              reviewedAt: taskSignDate,
              reviewedBy: "Reviewed before tracking",
            }
          : null);

      setStatus(taskStatus);
      setReviewDetails(resolvedReviewDetails);
    } catch (err) {
      console.error("Failed to fetch task status", err);
    }
  };

  useEffect(() => {
    if (userId && taskId) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, taskId, taskName, referenceId, strictReference, refreshTrigger]);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/dropdowns/user-tasks/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setAssignedTasks(res.data || []);
      } catch (e) {
        console.error("Failed to load user tasks", e);
      }
    })();
  }, [userId]);

  const orderedTasks = useMemo(() => assignedTasks || [], [assignedTasks]);

  const currentIndex = useMemo(() => {
    const idx = orderedTasks.findIndex(
      (t) => Number(t.ut_task_id) === Number(taskId)
    );
    return idx >= 0 ? idx : 0;
  }, [orderedTasks, taskId]);

  const goToTask = (task) => {
    if (!task) return;
    const viewingUserId = userId;
    const currentUserId = Number(localStorage.getItem("userId"));
    const adminRole = localStorage.getItem("userRole") === "1";

    if (task.t_key === "consent_history") {
      navigate(`/consent/list/${viewingUserId}`);
      return;
    }

    if (task.t_key === "user_information") {
      if (adminRole && currentUserId !== Number(viewingUserId)) {
        navigate(`/edit-user/${viewingUserId}`);
      } else {
        navigate(`/profile`);
      }
      return;
    }

    if (!task.t_route) return;
    const path = task.t_route
      .replace(":userId", String(viewingUserId))
      .replace(":id", String(viewingUserId));
    navigate(path);
  };

  const goNext = () => {
    if (orderedTasks.length === 0) return;
    const nextIndex = (currentIndex + 1) % orderedTasks.length;
    goToTask(orderedTasks[nextIndex]);
  };

  const handleJump = (e) => {
    const tid = Number(e.target.value);
    const t = orderedTasks.find((x) => Number(x.ut_task_id) === tid);
    goToTask(t);
  };

  const markReviewed = async () => {
    if (!isAdmin) return;

    try {
      let endpoint;

      switch (taskName) {
        case "vacation_form":
          endpoint = `${API_BASE}/api/vacationform/review/complete/${referenceId}`;
          break;
        case "cvsa_inspections":
          endpoint = `${API_BASE}/api/cvsa/review/complete/${referenceId}`;
          break;
        case "interview":
          endpoint = `${API_BASE}/api/interviews/review/complete/${userId}/${taskId}`;
          break;
        default:
          endpoint = `${API_BASE}/api/usertasks/review`;
          break;
      }

      const confirmReview = window.confirm(
        "Are you sure you want to mark this task as Reviewed?"
      );
      if (!confirmReview) return;

      try {
        setGlobalLoading?.(true);

        if (endpoint.includes("/complete/")) {
          await axios.put(endpoint, null, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
        } else {
          await axios.post(
            endpoint,
            { userId, taskId, referenceId },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
        }

        await fetchStatus();
        await onReviewed?.();
      } finally {
        setGlobalLoading?.(false);
      }

      await fetchStatus();
      alert("Task marked as reviewed!");
    } catch (err) {
      console.error("Failed to mark as reviewed", err);
      alert("Failed to mark as reviewed. Please try again.");
    }
  };

  const unreview = async () => {
    if (!isAdmin) return;
    const confirmUnreview = window.confirm(
      "Do you really want to move this task back to review?"
    );
    if (!confirmUnreview) return;

    try {
      let endpoint;
      switch (taskName) {
        case "vacation_form":
          endpoint = `${API_BASE}/api/vacationform/review/reset/${referenceId}`;
          break;
        case "cvsa_inspections":
          endpoint = `${API_BASE}/api/cvsa/review/reset/${referenceId}`;
          break;
        case "interview":
          endpoint = `${API_BASE}/api/interviews/review/reset/${userId}/${taskId}`;
          break;
        default:
          endpoint = `${API_BASE}/api/usertasks/unreview`;
          break;
      }

      try {
        setGlobalLoading?.(true);

        if (endpoint.includes("/reset/")) {
          await axios.put(endpoint, null, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
        } else {
          await axios.post(
            endpoint,
            { userId, taskId, referenceId },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
        }

        await fetchStatus();
        await onReviewed?.();
        alert("Task moved back to review.");
      } catch (err) {
        console.error("Failed to move back to review", err);
        alert("Failed to move back to review. Please try again.");
      } finally {
        setGlobalLoading?.(false);
      }
    } catch (err) {
      console.error("Failed to move back to review", err);
      alert("Failed to move back to review. Please try again.");
    }
  };

  const renderStatusLabel = () => {
    if (status === "expired") {
      return (
        <div className="status-wrapper">
          <div className="status-badge expired readonly">Expired</div>
        </div>
      );
    }

    if (status === "completed" || status === "almost_expired") {
      const reviewedAt = formatReviewedAt(reviewDetails?.reviewedAt);
      const reviewedBy = reviewDetails?.reviewedBy;

      return (
        <div className="status-wrapper">
          {isAdmin && (
            <div className="status-badge edit" onClick={unreview}>
              Unreview
            </div>
          )}
          <div className="status-badge completed">
            <div className="status-badge-content">
              <div className="status-badge-title">Reviewed</div>
              {reviewedAt ? (
                <div className="status-badge-meta">as of {reviewedAt}</div>
              ) : null}
              {reviewedBy ? (
                <div className="status-badge-meta">by {reviewedBy}</div>
              ) : null}
            </div>
            <div className="status-badge-check" aria-hidden="true">
              <span className="status-badge-check-mark">✓</span>
            </div>
          </div>
        </div>
      );
    }

    if (status === "review") {
      if (isAdmin) {
        return (
          <div className="status-wrapper">
            <div className="status-badge review clickable" onClick={markReviewed}>
              Mark as Reviewed
            </div>
          </div>
        );
      }
      return (
        <div className="status-wrapper">
          <div className="status-badge review readonly">Waiting for Review</div>
        </div>
      );
    }

    if (isAdmin) {
      return (
        <div className="status-wrapper">
          <div className="status-badge review clickable" onClick={markReviewed}>
            Mark as Reviewed
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="review-manager-bar mt-5" data-download-exclude>
      <div className="left-section">
        <select
          className="form-select form-select-sm d-inline w-auto"
          value={Number(taskId)}
          onChange={handleJump}
          disabled={orderedTasks.length === 0}
        >
          {orderedTasks.map((t) => (
            <option key={t.ut_task_id} value={t.ut_task_id}>
              {t.t_title || `Task ${t.ut_task_id}`}
            </option>
          ))}
        </select>

        <button
          className="btn btn-sm btn-light ms-2"
          onClick={goNext}
          disabled={orderedTasks.length === 0}
        >
          Next
        </button>
      </div>

      <div className="right-section">{renderStatusLabel()}</div>
    </div>
  );
};

export default ReviewManager;
